import React from 'react';
import { User, Mail, Phone, MapPin, Calendar, Award, Edit3, Settings, Shield } from 'lucide-react';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  user?: {
    name: string;
    email: string;
    avatar: string;
    role?: string;
    phone?: string;
    about?: string;
    location?: string;
    joinDate?: string;
    department?: string;
  };
  onLogout?: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose, user, onLogout }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#232336] rounded-2xl shadow-2xl p-8 w-full max-w-2xl relative">
        <button 
          className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors duration-200" 
          onClick={onClose}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white/10 shadow-2xl">
              <img 
                src={user?.avatar || 'https://randomuser.me/api/portraits/men/32.jpg'} 
                alt="avatar" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full border-2 border-[#232336] flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">{user?.name || 'John Doe'}</h2>
          <div className="flex items-center justify-center gap-2 text-white/60 mb-1">
            <Award size={16} />
            <span className="text-sm">{user?.role || 'Software Engineer'}</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
            <Mail size={14} />
            <span>{user?.email || 'john.doe@email.com'}</span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 mb-8">
          <button className="flex-1 flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-3 text-white transition-all duration-200 group">
            <Edit3 size={16} className="text-white/60 group-hover:text-white" />
            <span className="text-sm font-medium">Edit Profile</span>
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-3 text-white transition-all duration-200 group">
            <Settings size={16} className="text-white/60 group-hover:text-white" />
            <span className="text-sm font-medium">Settings</span>
          </button>
        </div>

        {/* Profile Information */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-purple-600">
                <User size={16} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Contact Information</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-white/70">
                <Mail size={16} className="text-white/40" />
                <span className="text-sm">{user?.email || 'john.doe@email.com'}</span>
              </div>
              {user?.phone && (
                <div className="flex items-center gap-3 text-white/70">
                  <Phone size={16} className="text-white/40" />
                  <span className="text-sm">{user.phone}</span>
                </div>
              )}
              {user?.location && (
                <div className="flex items-center gap-3 text-white/70">
                  <MapPin size={16} className="text-white/40" />
                  <span className="text-sm">{user.location}</span>
                </div>
              )}
            </div>
          </div>

          {/* Work Information */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-green-600 to-teal-600">
                <Award size={16} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Work Information</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-white/70">
                <Award size={16} className="text-white/40" />
                <span className="text-sm">{user?.role || 'Software Engineer'}</span>
              </div>
              {user?.department && (
                <div className="flex items-center gap-3 text-white/70">
                  <Shield size={16} className="text-white/40" />
                  <span className="text-sm">{user.department}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-white/70">
                <Calendar size={16} className="text-white/40" />
                <span className="text-sm">Joined {user?.joinDate || 'January 2023'}</span>
              </div>
            </div>
          </div>

          {/* About Section */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-pink-600 to-red-600">
                <User size={16} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">About</h3>
            </div>
            
            <div className="text-white/70 text-sm leading-relaxed">
              {user?.about || 'Passionate software engineer with expertise in modern web technologies. Always eager to learn new technologies and contribute to innovative projects.'}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="flex items-center justify-center gap-2 text-white/40 text-xs">
            <Shield size={12} />
            <span>Account verified • Last active today</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal; 